import React from "react";
import { Mountain, ArrowUpRight, Camera, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useSite } from "../context/SiteContext";
import useReveal from "../hooks/useReveal";
import Crest from "./Crest";

function Section({ section, index }) {
  const hasImage = Boolean(section.image);
  const flip = index % 2 === 1;
  return (
    <div
      data-testid={`cave-section-${section.id}`}
      className={`reveal grid gap-8 lg:gap-14 items-center ${hasImage ? "lg:grid-cols-2" : "lg:grid-cols-1"}`}
    >
      {hasImage && (
        <figure className={`img-zoom relative ${flip ? "lg:order-2" : ""}`}>
          <img src={section.image} alt={section.heading} className="w-full h-72 sm:h-[26rem] object-cover" />
          {section.credit && (
            <figcaption className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-ink/90 to-transparent px-4 py-3">
              <span className="font-label text-[0.5rem] uppercase tracking-[0.14em] text-parchment/60">{section.credit}</span>
            </figcaption>
          )}
        </figure>
      )}
      <div className={hasImage ? (flip ? "lg:order-1" : "") : "max-w-3xl"}>
        <h2 className="font-display text-3xl sm:text-4xl font-light leading-tight text-parchment">
          {section.heading}
        </h2>
        <div className="mt-5 space-y-4">
          {section.body.map((p, i) => (
            <p key={i} className="text-[1.02rem] leading-relaxed text-parchment/65 font-light">
              {p}
            </p>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function CavePage() {
  const { c } = useSite();
  return <ArticlePage data={c.cave} />;
}

export function ArticlePage({ data: cave }) {
  useReveal();
  const { c } = useSite();
  const navigate = useNavigate();

  return (
    <div className="bg-ink">
      {/* hero band */}
      <section className="relative min-h-[68vh] flex items-end grain overflow-hidden">
        <div className="absolute inset-0">
          <img src={cave.heroImage} alt={cave.eyebrow} className="w-full h-full object-cover animate-kenburns" />
          <div className="absolute inset-0 bg-gradient-to-b from-[rgba(18,15,11,0.75)] via-[rgba(18,15,11,0.5)] to-[rgba(18,15,11,0.97)]" />
        </div>
        <div className="relative max-w-[1220px] mx-auto px-5 sm:px-8 w-full pt-40 pb-14">
          <div className="flex items-center gap-3 mb-6 text-gold">
            <Mountain className="w-4 h-4" />
            <span className="eyebrow" data-testid="cave-eyebrow">{cave.eyebrow}</span>
          </div>
          <h1 className="font-display text-5xl sm:text-7xl font-light leading-[0.98] text-[#EFE7D6] max-w-3xl">
            {cave.title}
          </h1>
          <p className="mt-7 text-lg sm:text-xl leading-relaxed text-[#EFE7D6]/80 font-light max-w-2xl">
            {cave.intro}
          </p>
          {cave.heroCredit && (
            <span className="mt-6 inline-flex items-center gap-1.5 font-label text-[0.5rem] uppercase tracking-[0.14em] text-parchment/45">
              <Camera className="w-3 h-3" /> {cave.heroCredit}
            </span>
          )}
          {cave.mapFocus && (
            <div className="mt-8">
              <button
                data-testid="view-on-map-btn"
                onClick={() => navigate(`/?loc=${cave.mapFocus}#map`)}
                className="inline-flex items-center gap-2 border border-gold/50 text-gold px-6 py-3 font-label text-[0.62rem] uppercase tracking-[0.18em] hover:bg-gold hover:text-ink transition-colors duration-300"
              >
                <MapPin className="w-3.5 h-3.5" />
                {c.ui.viewOnMap}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* facts strip */}
      <section className="border-b border-[var(--c-border)] bg-ink2">
        <div className="max-w-[1220px] mx-auto px-5 sm:px-8">
          <dl className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6">
            {cave.facts.map((f, i) => (
              <div
                key={i}
                data-testid={`cave-fact-${i}`}
                className="px-5 py-7 border-b md:border-b-0 border-r border-[var(--c-border)] last:border-r-0"
              >
                <dt className="font-label text-[0.56rem] uppercase tracking-[0.2em] text-gold">{f.k}</dt>
                <dd className="mt-2 font-display text-xl text-parchment leading-tight">{f.v}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* narrative sections */}
      <section className="py-20 sm:py-28">
        <div className="max-w-[1220px] mx-auto px-5 sm:px-8 space-y-20 sm:space-y-28">
          {cave.sections.map((s, i) => (
            <Section key={s.id} section={s} index={i} />
          ))}
        </div>
      </section>

      {/* connection callout */}
      <section className="border-y border-[var(--c-border)] bg-ink2">
        <div className="max-w-[1220px] mx-auto px-5 sm:px-8 py-20 sm:py-24">
          <div className="reveal flex flex-col sm:flex-row items-start gap-7 max-w-4xl">
            <Crest size={52} className="shrink-0" />
            <div>
              <h2 className="font-display italic text-3xl sm:text-4xl text-gold leading-tight">
                {cave.connection.heading}
              </h2>
              <p className="mt-5 text-lg leading-relaxed text-parchment/70 font-light">
                {cave.connection.body}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* gallery */}
      <section className="py-20 sm:py-28">
        <div className="max-w-[1220px] mx-auto px-5 sm:px-8">
          <h2 className="font-display text-3xl sm:text-4xl font-light text-parchment mb-10">{cave.galleryTitle}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {cave.gallery.map((g, i) => (
              <figure
                key={i}
                data-testid={`cave-gallery-${i}`}
                className="reveal img-zoom relative border border-[var(--c-border)] bg-ink2"
                style={{ animationDelay: `${(i % 2) * 90}ms` }}
              >
                <img src={g.src} alt={g.caption} className="w-full h-64 sm:h-80 object-cover" />
                <figcaption className="px-5 py-4 border-t border-[var(--c-border)]">
                  <p className="font-display italic text-lg text-parchment/85 leading-snug">{g.caption}</p>
                  {g.credit && (
                    <span className="mt-2 block font-label text-[0.5rem] uppercase tracking-[0.14em] text-parchment/45">
                      {g.credit}
                    </span>
                  )}
                </figcaption>
              </figure>
            ))}
          </div>

          {/* credits & sources */}
          <div className="mt-16 flex flex-col sm:flex-row items-start gap-6 border-t border-[var(--c-border)] pt-10">
            <Crest size={40} className="shrink-0 opacity-70" />
            <div className="max-w-3xl">
              <h3 className="font-label text-[0.62rem] uppercase tracking-[0.2em] text-gold mb-3">
                {cave.creditsTitle}
              </h3>
              <p className="text-sm text-parchment/50 font-light leading-relaxed">{cave.legalNote}</p>
              <div className="mt-5 flex flex-col gap-2">
                <span className="font-label text-[0.56rem] uppercase tracking-[0.18em] text-parchment/40">
                  {cave.sourcesLabel}
                </span>
                {cave.sources.map((s, i) => (
                  <a
                    key={i}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-parchment/70 hover:text-gold transition-colors font-light w-fit"
                  >
                    {s.label}
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
